// Shared no-QR stamp fallback — the Card-code + Search (phone/email) tabs
// used when a customer's pass can't be scanned (online / delivery orders, a
// damaged QR, a flat phone). Extracted from the owner StampPage so the staff
// PWA scan page can reuse the exact same UI.
//
// The only thing that differs between owner and staff is the search endpoint
// (`searchPath`) — owner hits /owner/customers/search, staff hits
// /staff/customers/search. Both return the same `{ results: StampCard[] }`
// shape and resolve to the shared StampCard type. HTTP goes through
// `ownerApi` either way (the staff tree already imports it).

import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { ownerApi, OwnerApiError } from '../../owner/ownerApi.js'
import type { StampCard } from '../../owner/components/StampConfirmOverlay.js'
import '../../owner/pages/stampPage.css'

export type StampFallbackMode = 'code' | 'search'

const CARD_CODE_REGEX = /^[A-Z]{3}-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/

/**
 * Format a raw card-code entry into the canonical XXX-XXXX-XXXX shape as the
 * user types. They can type with or without dashes — we strip every
 * non-alphanumeric char, uppercase, cap at 11 chars, and re-insert the two
 * dashes at the fixed segment boundaries (after char 3 and char 7).
 */
function formatCardCode(raw: string): string {
  const chars = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 11)
  if (chars.length <= 3) return chars
  if (chars.length <= 7) return `${chars.slice(0, 3)}-${chars.slice(3)}`
  return `${chars.slice(0, 3)}-${chars.slice(3, 7)}-${chars.slice(7)}`
}

export function StampFallbackForm({
  mode,
  onModeChange,
  onResolved,
  onBack,
  searchPath,
  onRecover,
}: {
  mode: StampFallbackMode
  onModeChange: (m: StampFallbackMode) => void
  onResolved: (c: StampCard) => void
  onBack: () => void
  /** The customer-search endpoint for the current role. */
  searchPath: '/owner/customers/search' | '/staff/customers/search'
  /** Staff scan page only. Results whose program lets staff recover passes
   *  (`staff_can_recover_pass`) get a "Recover pass" button. */
  onRecover?: ((c: StampCard) => void) | undefined
}) {
  return (
    <div className="stamp-fallback">
      <button type="button" className="stamp-fallback-back" onClick={onBack}>
        ← Back to scanner
      </button>
      <div className="stamp-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'code'}
          className={`stamp-tab${mode === 'code' ? ' is-selected' : ''}`}
          onClick={() => onModeChange('code')}
        >
          Card code
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'search'}
          className={`stamp-tab${mode === 'search' ? ' is-selected' : ''}`}
          onClick={() => onModeChange('search')}
        >
          Search
        </button>
      </div>
      <div className="stamp-tab-body">
        {mode === 'code' && (
          <CardCodeTab onResolved={onResolved} searchPath={searchPath} />
        )}
        {mode === 'search' && (
          <SearchTab
            onResolved={onResolved}
            searchPath={searchPath}
            onRecover={onRecover}
          />
        )}
      </div>
    </div>
  )
}

function CardCodeTab({
  onResolved,
  searchPath,
}: {
  onResolved: (c: StampCard) => void
  searchPath: string
}) {
  const [value, setValue] = useState('')
  const upper = value.trim().toUpperCase()
  const valid = CARD_CODE_REGEX.test(upper)

  const lookup = useMutation({
    mutationFn: async () => {
      const res = await ownerApi.get<{ results: StampCard[] }>(
        `${searchPath}?q=${encodeURIComponent(upper)}`,
      )
      return res.results
    },
    onSuccess: (results) => {
      if (results.length === 0) {
        toast.error('No card found with that code')
        return
      }
      onResolved(results[0]!)
    },
    onError: (err) => {
      const message =
        err instanceof OwnerApiError ? err.message : 'Lookup failed'
      toast.error(message)
    },
  })

  return (
    <form
      className="stamp-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (valid && !lookup.isPending) lookup.mutate()
      }}
    >
      <label htmlFor="stamp-card-code" className="stamp-label">
        Card code
      </label>
      <input
        id="stamp-card-code"
        className="owner-input stamp-code-input"
        type="text"
        inputMode="text"
        autoCapitalize="characters"
        placeholder="GTC-AB23-CDEF"
        autoComplete="off"
        autoFocus
        value={value}
        onChange={(e) => setValue(formatCardCode(e.target.value))}
        spellCheck={false}
      />
      <p className="stamp-hint">
        The 11-character code printed below the QR on the customer's pass.
        Type it straight through — the dashes are added for you.
      </p>
      <button
        type="submit"
        className="owner-btn owner-btn-primary"
        disabled={!valid || lookup.isPending}
      >
        {lookup.isPending ? 'Looking up…' : 'Find card'}
      </button>
    </form>
  )
}

function SearchTab({
  onResolved,
  searchPath,
  onRecover,
}: {
  onResolved: (c: StampCard) => void
  searchPath: string
  onRecover?: ((c: StampCard) => void) | undefined
}) {
  const [value, setValue] = useState('')
  const trimmed = value.trim()

  const [debounced, setDebounced] = useState(trimmed)
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(trimmed), 300)
    return () => window.clearTimeout(handle)
  }, [trimmed])

  const query = useQuery({
    queryKey: ['customers', 'search', searchPath, debounced],
    queryFn: async () => {
      const res = await ownerApi.get<{ results: StampCard[] }>(
        `${searchPath}?q=${encodeURIComponent(debounced)}`,
      )
      return res.results
    },
    enabled: debounced.length >= 2,
  })

  return (
    <div className="stamp-form">
      <label htmlFor="stamp-search" className="stamp-label">
        Customer name, phone, or email
      </label>
      <input
        id="stamp-search"
        className="owner-input"
        type="text"
        placeholder="Maria, 09171234567, or maria@example.com"
        autoComplete="off"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        spellCheck={false}
      />
      {debounced.length >= 2 && (
        <div className="stamp-search-results">
          {query.isLoading && <p className="stamp-hint">Searching…</p>}
          {query.isSuccess && query.data.length === 0 && (
            <p className="stamp-hint">No customer found.</p>
          )}
          {query.isSuccess &&
            query.data.map((r) => (
              <div key={r.card_id} className="stamp-result-item">
                <button
                  type="button"
                  className="stamp-result-row"
                  onClick={() => onResolved(r)}
                >
                  <span className="stamp-result-name">
                    {r.first_name?.trim() || 'Customer'}
                    {r.status === 'completed' && (
                      <span
                        className="owner-pill owner-pill-completed"
                        style={{ marginLeft: 8 }}
                      >
                        Completed
                      </span>
                    )}
                  </span>
                  <span className="stamp-result-meta">
                    {r.card_code} · {r.stamps_count} / {r.total_stamps} stamps
                  </span>
                </button>
                {onRecover && r.staff_can_recover_pass && (
                  <button
                    type="button"
                    className="owner-btn owner-btn-sm stamp-result-recover"
                    onClick={() => onRecover(r)}
                  >
                    Recover pass
                  </button>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  )
}
